import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const clearAuthStorage = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_profile");
  localStorage.removeItem("authActiveTab");
};

const emitAuthExpired = () => {
  window.dispatchEvent(new CustomEvent("auth:expired"));
  window.dispatchEvent(new CustomEvent("userLogout"));
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network/offline/CORS timeout path
    if (!error.response) {
      window.dispatchEvent(
        new CustomEvent("api:offline", {
          detail: { message: error.message || "Network error" },
        })
      );
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes("token/refresh/") &&
      !originalRequest?.url?.includes("token/")
    ) {
      const refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        clearAuthStorage();
        emitAuthExpired();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}token/refresh/`,
          { refresh: refreshToken }
        );

        const newAccessToken = response.data.access;
        const newRefreshToken = response.data.refresh || refreshToken;

        localStorage.setItem("access_token", newAccessToken);
        localStorage.setItem("refresh_token", newRefreshToken);

        onRefreshed(newAccessToken);
        window.dispatchEvent(new CustomEvent("auth:recovered"));

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearAuthStorage();
        emitAuthExpired();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;